import type {
  Answers,
  Category,
  OverviewState,
  PlaybookId,
  Selection,
  TraceEntry,
} from './types';
import { RULES_VERSION, resolveAnswers } from './types';
import { playbooks } from './playbooks';

interface Candidate {
  id: PlaybookId;
  rank: number;
  reasons: string[];
}

const hasAny = <T,>(list: T[], ...vals: T[]) => vals.some((v) => list.includes(v));
const list = (values: string[]) => (values.length ? values.join(' + ') : 'nothing selected');

const attemptsExist = (a: Answers) =>
  !hasAny(a.response, 'NO_RECENT_ATTEMPT') && !a.introduction.includes('VERY_LITTLE');

const interestReported = (a: Answers) =>
  hasAny(a.response, 'SONG_ENQUIRIES', 'DELIBERATE_INTEREST', 'RETURNING_INTEREST');

/**
 * Deterministic selection. Pure function: same answers always produce the same
 * primary, secondaries, overview and trace. AI never changes any of this.
 *
 * Every question can hold several answers, so each answer is allowed to raise
 * its own candidate. The most limiting candidate becomes the primary and the
 * rest are reported as things to check next.
 */
export function selectPlaybooks(a: Answers): Selection {
  const r = resolveAnswers(a);
  const entries: TraceEntry[] = [];
  const gateNotes: string[] = [];
  const candidates: Candidate[] = [];

  const block = (id: PlaybookId, reason: string) =>
    entries.push({ playbookId: id, status: 'BLOCKED', reasons: [reason] });

  const add = (id: PlaybookId, rank: number, reasons: string[]) =>
    candidates.push({ id, rank, reasons });

  // ---- Gate 0: multi-answer reducers, recorded so the choice is auditable ----
  if (a.readiness.length > 1) {
    gateNotes.push(
      `Gate 0 (multiple answers): readiness = ${list(a.readiness)}. Worked from the most advanced: ${r.readiness}.`,
    );
  }
  if (a.capacity.length > 1) {
    gateNotes.push(
      `Gate 0 (multiple answers): capacity = ${list(a.capacity)}. Worked from the smallest: ${r.capacity}.`,
    );
  }

  // ---- Gate 1: readiness ----
  if (r.readiness === 'NO_SHAREABLE') {
    gateNotes.push('Gate 1 (readiness): NO_SHAREABLE. Preparatory route taken before any promotional diagnosis.');
    entries.push({
      playbookId: 'B02',
      status: 'SELECTED_PRIMARY',
      reasons: ['Q1 readiness = NO_SHAREABLE', 'All other categories blocked: nothing exists to promote.'],
    });
    return {
      primary: 'B02',
      secondaries: [],
      overview: buildOverview(a, 'B02', []),
      trace: {
        rulesVersion: RULES_VERSION,
        gateReached: 'Gate 1: readiness',
        gateNotes,
        entries,
        tieRuleApplied: null,
      },
    };
  }
  gateNotes.push(`Gate 1 (readiness): ${list(a.readiness)}. Promotional routes allowed.`);

  // ---- Gate 2: decisive contradiction ----
  const contradiction =
    r.readiness === 'UNRELEASED_SHAREABLE' && a.nextStep.includes('CLEAR_DESTINATION')
      ? 'Reported a clear listening destination for a song that is not released. Resolved in favour of the release status, not the more alarming reading.'
      : null;
  if (contradiction) gateNotes.push(`Gate 2 (contradiction): ${contradiction}`);
  else gateNotes.push('Gate 2 (contradiction): none decisive.');

  // ---- Gate 3: concrete access friction ----
  if (a.nextStep.includes('UNRELEASED_UNCLEAR') || (contradiction && r.readiness === 'UNRELEASED_SHAREABLE')) {
    add('N03', 11, [
      `Q6 next step = ${list(a.nextStep)}`,
      `Q1 readiness = ${list(a.readiness)}`,
      'No usable action exists for an unreleased song.',
    ]);
  } else {
    block('N03', 'Song is released, or a usable action already exists.');
  }

  if (hasAny(a.nextStep, 'NO_ROUTE', 'UNKNOWN')) {
    add('N01', 10, [
      `Q6 next step = ${list(a.nextStep)}`,
      'Artist reports an absent or unknown destination, which is concrete access friction.',
    ]);
  } else {
    block('N01', `Q6 = ${list(a.nextStep)}: no confirmed absent or mismatched destination. A general link page alone is not evidence.`);
  }

  if (a.nextStep.includes('MANY_ACTIONS')) {
    add('N02', 12, ['Q6 next step includes MANY_ACTIONS', 'Several competing requests reported.']);
  } else {
    block('N02', `Q6 = ${list(a.nextStep)}: no competing-request pattern reported.`);
  }

  // ---- Gate 4: strongest specific reported gap at the stage reached ----
  // Return requires reported song-specific interest.
  if (interestReported(a) && hasAny(a.return, 'MOVE_ON', 'NO_FOLLOW_UP')) {
    add('R01', 25, [
      `Q5 response = ${list(a.response)}`,
      `Q7 return = ${list(a.return)}`,
      'Reported interest exists and no follow-up is planned.',
    ]);
  } else {
    block('R01', interestReported(a) ? `Q7 = ${list(a.return)}: a follow-up already exists.` : 'No reported song-specific interest, so retention cannot be the bottleneck.');
  }

  if (interestReported(a) && a.return.includes('SAME_REQUEST')) {
    add('R02', 26, ['Q7 return includes SAME_REQUEST', 'Reported interest exists but the follow-up repeats the same ask.']);
  } else {
    block('R02', 'Requires reported interest plus a repeated identical request.');
  }

  if (a.response.includes('RETURNING_INTEREST') && a.personalisation.available_next && a.personalisation.available_next !== 'NONE') {
    add('R03', 27, ['Q5 response includes RETURNING_INTEREST', 'A related available item was supplied.']);
  } else {
    block('R03', 'Requires returning interest plus a supplied related song, performance or confirmed event.');
  }

  // Discovery: no route to new people at all.
  if (hasAny(a.discovery, 'EXISTING_CIRCLE', 'NOT_STARTED')) {
    add('D01', 20, [
      `Q3 discovery = ${list(a.discovery)}`,
      'No deliberate route to unfamiliar listeners, so downstream stages cannot be judged yet.',
    ]);
  } else {
    block('D01', `Q3 = ${list(a.discovery)}: a route to new people is already described.`);
  }

  if (
    hasAny(a.discovery, 'COLLAB', 'CURATION') &&
    hasAny(a.goal, 'MORE_DISCOVERY', 'FIRST_LISTENERS', 'NEXT_RELEASE')
  ) {
    add('D02', 50, [`Q3 discovery = ${list(a.discovery)}`, 'Route runs through other people, so relevance to the recipient is the variable.']);
  } else {
    block('D02', 'Requires a chosen collaboration or curation route.');
  }

  if (a.discovery.includes('SCATTERED') && (hasAny(a.capacity, 'TINY', 'SMALL') || hasAny(a.learning, 'RANDOM', 'CHANGE_EVERYTHING'))) {
    add('D03', 51, [
      'Q3 discovery includes SCATTERED',
      `Q9 capacity = ${list(a.capacity)}`,
      'Confirmed inability to execute or compare, not simply the use of more than one channel.',
    ]);
  } else {
    block('D03', 'Requires SCATTERED plus a confirmed capacity or comparison problem.');
  }

  // Content: only when an online content route is actually in use.
  const onlineRoute = !a.introduction.includes('NOT_ONLINE');
  if (!onlineRoute) {
    gateNotes.push('Gate 5 (feasibility): NOT_ONLINE reported with a working offline route. Content playbooks blocked.');
  }

  if (onlineRoute && a.introduction.includes('ANNOUNCEMENTS') && !a.introduction.includes('SONG_LED')) {
    add('C01', 30, [
      'Q4 introduction includes ANNOUNCEMENTS without SONG_LED',
      'The song is described rather than heard, and a music-first version has not been tested.',
    ]);
  } else {
    block('C01', onlineRoute ? 'Requires announcement-dominant material with no song-led material.' : 'Online content is not the artist route (Q4 = NOT_ONLINE).');
  }

  if (onlineRoute && a.introduction.includes('OTHER_CONTENT') && a.response.includes('LIGHT_ATTENTION')) {
    add('C02', 31, [
      'Q4 introduction includes OTHER_CONTENT',
      'Q5 response includes LIGHT_ATTENTION',
      'Attention exists but is reported as separate from the music.',
    ]);
  } else {
    block('C02', onlineRoute ? 'Requires separate-from-music content plus reported attention on it.' : 'Online content is not the artist route.');
  }

  if (onlineRoute && a.introduction.includes('VERY_LITTLE') && hasAny(a.capacity, 'TINY', 'SMALL')) {
    add('C03', 32, [
      'Q4 introduction includes VERY_LITTLE',
      `Q9 capacity = ${list(a.capacity)}`,
      'Execution cost is the reported obstacle, not strategy.',
    ]);
  } else {
    block('C03', 'Requires very little recent material plus a limited time allowance.');
  }

  // Audience
  if (hasAny(a.audience, 'EVERYONE', 'UNSURE')) {
    if (a.readiness.includes('CHOOSE_SONG') && r.readiness === 'CHOOSE_SONG') {
      add('A03', 62, [`Q2 audience = ${list(a.audience)}`, 'Q1 readiness = CHOOSE_SONG: no single song chosen, so an entry point is needed first.']);
      block('A01', 'A03 covers this case because no single song has been chosen.');
    } else {
      add('A01', 60, [`Q2 audience = ${list(a.audience)}`, 'No listener hypothesis exists for this song.']);
      block('A03', 'Music was not reported as crossing styles, and a single song is chosen.');
    }
  } else {
    block('A01', `Q2 = ${list(a.audience)}: a listener direction is already described.`);
    block('A03', 'Requires an unchosen song or a genuinely mixed sound.');
  }

  if (hasAny(a.audience, 'REFERENCE_NAMES', 'TOPIC_FIRST')) {
    add('A02', 61, [`Q2 audience = ${list(a.audience)}`, 'Targeting rests on references or subject rather than a musical reason.']);
  } else {
    block('A02', `Q2 = ${list(a.audience)}: not a reference-led or topic-led description.`);
  }

  // Testing: requires real attempts.
  const testingAllowed = attemptsExist(a);
  if (!testingAllowed) gateNotes.push('Testing blocked: no real attempts to make more informative.');

  if (testingAllowed && a.learning.includes('CHANGE_EVERYTHING')) {
    add('T01', 70, ['Q8 learning includes CHANGE_EVERYTHING', 'Real attempts exist and can be made comparable.']);
  } else {
    block('T01', testingAllowed ? `Q8 = ${list(a.learning)}: variables are not all changing between attempts.` : 'No real attempts yet.');
  }

  if (testingAllowed && a.learning.includes('VIEWS_ONLY')) {
    add('T02', 71, ['Q8 learning includes VIEWS_ONLY', `Q1 goal = ${list(a.goal)}, which views sit a long way from.`]);
  } else {
    block('T02', testingAllowed ? `Q8 = ${list(a.learning)}: decisions are not view-led.` : 'No real attempts yet.');
  }

  if (testingAllowed && a.learning.includes('REPEAT_FEELING')) {
    add('T03', 72, ['Q8 learning includes REPEAT_FEELING', 'A promising attempt exists that has not been repeated comparably.']);
  } else {
    block('T03', testingAllowed ? `Q8 = ${list(a.learning)}: not a single unrepeated success pattern.` : 'No real attempts yet.');
  }

  // Baseline
  const thinEvidence =
    hasAny(a.response, 'NO_RECENT_ATTEMPT', 'UNKNOWN') || a.learning.includes('TOO_EARLY') || a.introduction.length === 0;

  // ---- Gate 6: resolve ----
  let primary: PlaybookId;
  let secondaries: PlaybookId[] = [];
  let tieRuleApplied: string | null = null;
  let gateReached: string;

  const coherent =
    a.audience.includes('SPECIFIC_REASON') &&
    a.nextStep.includes('CLEAR_DESTINATION') &&
    a.return.includes('RELATED_FOLLOW_UP') &&
    a.learning.includes('COMPARE') &&
    !hasAny(a.discovery, 'EXISTING_CIRCLE', 'NOT_STARTED', 'UNKNOWN') &&
    candidates.length === 0;

  if (candidates.length === 0 && coherent) {
    gateReached = 'Gate 6: no supported issue, coherent answers';
    primary = 'NO_CLEAR_GAP';
    entries.push({ playbookId: 'NO_CLEAR_GAP', status: 'SELECTED_PRIMARY', reasons: ['No category met its eligibility bar and the answers describe a coherent approach.'] });
    block('B01', 'Answers describe real activity and comparison, so a first-signals baseline would understate the situation.');
  } else if (candidates.length === 0) {
    gateReached = 'Gate 6: no supported issue, thin evidence';
    primary = 'B01';
    entries.push({ playbookId: 'B01', status: 'SELECTED_PRIMARY', reasons: ['No category met its eligibility bar.', thinEvidence ? 'Reported evidence is too thin to name a bottleneck.' : 'No concrete reported problem.'] });
    block('NO_CLEAR_GAP', 'Answers do not describe a coherent working approach across all six areas.');
  } else {
    candidates.sort((x, y) => x.rank - y.rank);
    const [best, ...rest] = candidates;
    primary = best.id;
    gateReached = candidates.length > 1 ? 'Gate 6: ranked candidates' : 'Gate 4: single supported gap';

    const tied = rest.filter((c) => c.rank === best.rank);
    if (tied.length) {
      tieRuleApplied = `Equal rank between ${best.id} and ${tied.map((t) => t.id).join(', ')}. Stable editorial preference ${RULES_VERSION} chose ${best.id}. We are choosing a first test, not identifying the cause.`;
    }

    // Everything else that cleared its bar becomes a 'next to check' item,
    // one per category so the list stays readable.
    const usedCategories = new Set<Category>([playbooks[best.id].category]);
    rest.forEach((c) => {
      const cat = playbooks[c.id].category;
      if (usedCategories.has(cat)) return;
      usedCategories.add(cat);
      secondaries.push(c.id);
    });

    entries.push({ playbookId: best.id, status: 'SELECTED_PRIMARY', reasons: best.reasons });
    rest.forEach((c) =>
      entries.push({
        playbookId: c.id,
        status: secondaries.includes(c.id) ? 'SELECTED_SECONDARY' : 'ELIGIBLE_NOT_SELECTED',
        reasons: secondaries.includes(c.id)
          ? [...c.reasons, 'Independent supporting answers, different category, reported as a later check rather than a second simultaneous test.']
          : [...c.reasons, `Same category as a higher-priority candidate, or lower priority than ${best.id} at this stage of the journey.`],
      }),
    );
    block('B01', 'A supported candidate exists.');
    block('NO_CLEAR_GAP', 'A supported candidate exists.');
  }

  return {
    primary,
    secondaries,
    overview: buildOverview(a, primary, secondaries),
    trace: { rulesVersion: RULES_VERSION, gateReached, gateNotes, entries, tieRuleApplied },
  };
}

function buildOverview(a: Answers, primary: PlaybookId, secondaries: PlaybookId[]) {
  const primaryCat = playbooks[primary].category;
  const secondaryCats = new Set(secondaries.map((id) => playbooks[id].category));

  const state = (cat: Category, fallback: OverviewState, note: string) => {
    if (cat === primaryCat) return { state: 'START_HERE' as OverviewState, note };
    if (secondaryCats.has(cat)) return { state: 'NEXT_TO_CHECK' as OverviewState, note };
    return { state: fallback, note };
  };

  const noShareable = a.readiness.includes('NO_SHAREABLE');
  const specificAudience = a.audience.includes('SPECIFIC_REASON');

  return {
    AUDIENCE: state(
      'AUDIENCE',
      specificAudience ? 'NO_CLEAR_ISSUE_REPORTED' : a.audience.includes('UNSURE') ? 'NOT_ENOUGH_INFORMATION' : 'NO_CLEAR_ISSUE_REPORTED',
      specificAudience
        ? 'You described a listener group and why the song fits. Self-reported, not verified.'
        : 'You have a direction, but it has not been narrowed or tested.',
    ),
    DISCOVERY: state(
      'DISCOVERY',
      a.discovery.includes('UNKNOWN') ? 'NOT_ENOUGH_INFORMATION' : 'NO_CLEAR_ISSUE_REPORTED',
      a.discovery.includes('UNKNOWN')
        ? 'You cannot yet say where new listeners come from.'
        : 'You described a route to new people. Whether it is working is a separate question.',
    ),
    CONTENT: state(
      'CONTENT',
      a.introduction.includes('NOT_ONLINE') ? 'NOT_RELEVANT_YET' : a.introduction.length === 0 ? 'NOT_ENOUGH_INFORMATION' : 'NO_CLEAR_ISSUE_REPORTED',
      a.introduction.includes('NOT_ONLINE')
        ? 'Online content is not your main route, so this is not a gap for you right now.'
        : 'Nothing in your answers points at how you introduce the song as the first thing to change.',
    ),
    NEXT_STEP: state(
      'NEXT_STEP',
      a.nextStep.includes('UNKNOWN') ? 'NOT_ENOUGH_INFORMATION' : 'NO_CLEAR_ISSUE_REPORTED',
      a.nextStep.includes('CLEAR_DESTINATION')
        ? 'You report one clear action and an easy route. We have not opened any link.'
        : 'No confirmed problem with the route you described.',
    ),
    RETURN: state(
      'RETURN',
      a.return.includes('NOT_YET') || noShareable ? 'NOT_RELEVANT_YET' : 'NO_CLEAR_ISSUE_REPORTED',
      a.return.includes('NOT_YET')
        ? 'You have not reached this part of the journey yet. That is not a failure.'
        : 'A follow-up exists, or there is no reported interest to follow up yet.',
    ),
    TESTING: state(
      'TESTING',
      !attemptsExist(a) || a.learning.includes('TOO_EARLY') ? 'NOT_RELEVANT_YET' : 'NO_CLEAR_ISSUE_REPORTED',
      !attemptsExist(a) || a.learning.includes('TOO_EARLY')
        ? 'Not enough attempts yet to compare. This is a stage, not a fault.'
        : 'You described a way of deciding what to repeat.',
    ),
  };
}

export const overviewLabels: Record<OverviewState, string> = {
  START_HERE: 'Start here',
  NEXT_TO_CHECK: 'Next to check',
  NO_CLEAR_ISSUE_REPORTED: 'No clear issue reported',
  NOT_ENOUGH_INFORMATION: 'Not enough information',
  NOT_RELEVANT_YET: 'Not relevant yet',
};
